import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import { io, Socket } from 'socket.io-client';
import 'leaflet/dist/leaflet.css';

interface DisplayMachine {
  id: string;
  code: string;
  location: string;
  city: string | null;
  status: string;
  latitude: number | null;
  longitude: number | null;
}

// Status -> color/label. Mirrors the palette used across the admin UI.
const STATUS: Record<string, { color: string; label: string }> = {
  online: { color: '#22c55e', label: 'Disponível' },
  in_use: { color: '#3b82f6', label: 'Em Uso' },
  maintenance: { color: '#eab308', label: 'Em Manutenção' },
  offline: { color: '#ef4444', label: 'Desligada' },
};

const statusOf = (s: string) => STATUS[s] ?? { color: '#9ca3af', label: s };

// A clean status indicator for the header: SVG dot + count + label on a subtle pill.
const StatusChip: React.FC<{ color: string; label: string; count: number }> = ({ color, label, count }) => (
  <div className="flex items-center gap-2 rounded-full bg-black/20 px-3 py-1">
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <circle cx="7" cy="7" r="6" fill={color} opacity="0.25" />
      <circle cx="7" cy="7" r="3.5" fill={color} />
    </svg>
    <span className="text-xl font-bold tabular-nums leading-none">{count}</span>
    <span className="text-xs font-medium uppercase tracking-wide text-white/80 leading-none">{label}</span>
  </div>
);

// City-center fallbacks so selecting a city pans there even when its machines
// have no coordinates cadastradas yet.
const CITY_CENTERS: Record<string, [number, number]> = {
  'São Paulo': [-23.5505, -46.6333],
  'Campinas': [-22.9099, -47.0626],
  'Rio de Janeiro': [-22.9068, -43.1729],
  'Belo Horizonte': [-19.9167, -43.9345],
  'São Bernardo do Campo': [-23.6914, -46.5646],
  'Guarulhos': [-23.4543, -46.5337],
  'Santo André': [-23.6639, -46.5383],
};

const getToken = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('token');
  if (fromUrl) {
    localStorage.setItem('displayToken', fromUrl);
    return fromUrl;
  }
  return localStorage.getItem('displayToken');
};

const wsUrl = (import.meta.env.VITE_API_URL || 'http://localhost:3001/api').replace('/api', '');
const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Auto-fit the map when the selected city or the set of plotted coordinates
// changes. The `fitKey` is a stable string so unrelated re-renders (e.g. the
// header clock ticking every second) do NOT re-fit and steal the user's zoom.
const FitBounds: React.FC<{
  coords: [number, number][];
  fitKey: string;
  fallback: [number, number] | null;
}> = ({ coords, fitKey, fallback }) => {
  const map = useMap();
  useEffect(() => {
    if (coords.length === 1) {
      map.setView(coords[0], 14);
    } else if (coords.length > 1) {
      map.fitBounds(coords, { padding: [60, 60] });
    } else if (fallback) {
      // City selected but none of its machines have coordinates yet.
      map.setView(fallback, 12);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey]);
  return null;
};

export const TvMapPage: React.FC = () => {
  const [machines, setMachines] = useState<DisplayMachine[]>([]);
  const [selectedCity, setSelectedCity] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState<Date>(new Date());
  const socketRef = useRef<Socket | null>(null);
  const token = useMemo(getToken, []);

  const fetchMachines = React.useCallback(async () => {
    if (!token) {
      setError('Token de exibição ausente. Abra esta página com ?token=...');
      return;
    }
    try {
      const res = await fetch(`${apiBase}/display/machines?token=${encodeURIComponent(token)}`);
      if (!res.ok) {
        setError(res.status === 401 ? 'Token de exibição inválido.' : 'Falha ao carregar unidades.');
        return;
      }
      const data = await res.json();
      setMachines(data.machines || []);
      setError(null);
    } catch {
      setError('Falha ao conectar ao servidor.');
    }
  }, [token]);

  // Initial load + 30s polling fallback (keeps the TV fresh if the socket drops).
  useEffect(() => {
    fetchMachines();
    const poll = setInterval(fetchMachines, 30000);
    const clock = setInterval(() => setNow(new Date()), 1000);
    return () => {
      clearInterval(poll);
      clearInterval(clock);
    };
  }, [fetchMachines]);

  // Live status updates via the admin-dashboard broadcast room.
  useEffect(() => {
    const socket = io(wsUrl, { transports: ['websocket', 'polling'], reconnection: true });
    socketRef.current = socket;
    socket.on('connect', () => socket.emit('join-admin-dashboard'));

    const applyStatus = (id: string, status: string) =>
      setMachines((prev) => prev.map((m) => (m.id === id ? { ...m, status } : m)));

    socket.on('machine-update', (msg: any) => {
      const m = msg?.data ?? msg;
      if (m?.id && m?.status) applyStatus(m.id, m.status);
    });
    socket.on('session-update', () => fetchMachines());
    socket.on('machine-heartbeat', (msg: any) => {
      const m = msg?.data ?? msg;
      if (m?.id && m?.status) applyStatus(m.id, m.status);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [fetchMachines]);

  const cities = useMemo(() => {
    const set = new Set<string>();
    machines.forEach((m) => m.city && set.add(m.city));
    return Array.from(set).sort();
  }, [machines]);

  const visible = useMemo(
    () => machines.filter((m) => selectedCity === 'all' || m.city === selectedCity),
    [machines, selectedCity],
  );

  const plotted = useMemo(
    () => visible.filter((m) => m.latitude != null && m.longitude != null),
    [visible],
  );
  const missing = visible.length - plotted.length;

  // Stable coords + key: only changes when the actual set of positions or the
  // selected city changes — so the clock re-render never re-fits the map.
  const coords = useMemo(
    () => plotted.map((m) => [m.latitude as number, m.longitude as number] as [number, number]),
    [plotted],
  );
  const fitKey = useMemo(
    () => selectedCity + '|' + coords.map((c) => c.join(',')).sort().join(';'),
    [selectedCity, coords],
  );
  const cityFallback = selectedCity !== 'all' ? CITY_CENTERS[selectedCity] ?? null : null;

  const counts = useMemo(() => {
    const c = { online: 0, in_use: 0, maintenance: 0, offline: 0 };
    visible.forEach((m) => {
      if (m.status in c) (c as any)[m.status]++;
    });
    return c;
  }, [visible]);

  return (
    <div className="fixed inset-0 flex flex-col bg-gray-900 text-white">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 bg-gradient-to-r from-orange-700 to-orange-500 shadow-lg">
        <div className="flex items-center gap-4">
          <img src="/assets/upcar-logo-preto.png" alt="UpCar Aspiradores" className="h-12 w-auto drop-shadow-lg" />
          <h1 className="text-2xl font-bold tracking-wide">Monitor de Unidades</h1>
        </div>
        <div className="flex items-center gap-3">
          <StatusChip color={STATUS.online.color} label={STATUS.online.label} count={counts.online} />
          <StatusChip color={STATUS.in_use.color} label={STATUS.in_use.label} count={counts.in_use} />
          <StatusChip color={STATUS.maintenance.color} label={STATUS.maintenance.label} count={counts.maintenance} />
          <StatusChip color={STATUS.offline.color} label={STATUS.offline.label} count={counts.offline} />
          <span className="ml-2 font-mono text-2xl tabular-nums">{now.toLocaleTimeString('pt-BR')}</span>
        </div>
      </div>

      {/* City selector */}
      <div className="flex flex-wrap items-center gap-2 px-6 py-2 bg-gray-800 border-b border-gray-700">
        <button
          onClick={() => setSelectedCity('all')}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
            selectedCity === 'all' ? 'bg-orange-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Todas as cidades
        </button>
        {cities.map((city) => (
          <button
            key={city}
            onClick={() => setSelectedCity(city)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
              selectedCity === city ? 'bg-orange-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {city}
          </button>
        ))}
      </div>

      {/* Map */}
      <div className="relative flex-1">
        {error && (
          <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-gray-900/90">
            <p className="text-xl text-red-300">{error}</p>
          </div>
        )}
        <MapContainer center={[-15.78, -47.93]} zoom={4} className="h-full w-full" zoomControl={false}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap"
          />
          <FitBounds coords={coords} fitKey={fitKey} fallback={cityFallback} />
          {plotted.map((m) => {
            const s = statusOf(m.status);
            return (
              <CircleMarker
                key={m.id}
                center={[m.latitude as number, m.longitude as number]}
                radius={11}
                pathOptions={{ color: '#fff', weight: 2, fillColor: s.color, fillOpacity: 0.9 }}
              >
                <Tooltip permanent direction="top" offset={[0, -8]} className="tv-map-label">
                  <span className="font-bold">{m.code}</span> · {m.location}
                </Tooltip>
              </CircleMarker>
            );
          })}
        </MapContainer>

        {/* Missing-coordinates counter */}
        {missing > 0 && (
          <div className="absolute bottom-4 left-4 z-[1000] rounded-lg bg-gray-800/90 px-4 py-2 text-sm text-gray-300">
            {missing} unidade(s) sem localização cadastrada
          </div>
        )}
      </div>
    </div>
  );
};
